import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  DuplicateCandidate,
  MergeAllJob,
  ProspectResponse,
} from "../../../../types/api";
import {
  bulkMergeProspects,
  cancelMergeAllDuplicates,
  dismissDuplicateCandidate,
  getDuplicateCandidates,
  getMergeAllJob,
  getProspect,
  startMergeAllDuplicates,
} from "../../../../services/prospects";
import { getSafeErrorMessage } from "../../../../utils/errorHandler";
import { Button } from "../../../../components/Button";
import { ErrorBoundary } from "../../../../components/ErrorBoundary";
import { MergeProspectsModal } from "../players/merge/MergeProspectsModal";
import { DuplicateGroupCard } from "./DuplicateGroupCard";
import { BulkMergeConfirmDialog } from "./BulkMergeConfirmDialog";
import { DismissedDuplicatesPanel } from "./DismissedDuplicatesPanel";
import { tourAnchor } from "../../../tours/tourAnchors";
import { useTour } from "../../../tours/useTour";
import {
  BULK_MERGE_BATCH_SIZE,
  buildBulkMergeGroups,
  chunk,
  pickBulkSurvivor,
  pruneSelection,
  toggleSelection,
} from "./duplicateSelection";
import { useDuplicateCount } from "../../../../hooks/useDuplicateCount";
import "./DuplicatesSection.css";

const PAGE_SIZE = 20;

/** How often a live server-side run is polled. */
const MERGE_ALL_POLL_MS = 3000;

const isMergeAllRunning = (job: MergeAllJob | null): boolean =>
  job?.status === "queued" || job?.status === "running";

/**
 * The finished run whose summary was dismissed. The server keeps that job as the
 * organization's most recent one, so without remembering the dismissal every read
 * of it brings the banner straight back.
 */
const ACKNOWLEDGED_JOB_KEY = "rosterpilot.mergeAll.acknowledgedJob";

const readAcknowledgedJobId = (): string | null => {
  try {
    return window.localStorage.getItem(ACKNOWLEDGED_JOB_KEY);
  } catch {
    // Private mode or storage disabled — the summary just shows again.
    return null;
  }
};

type Tab = "open" | "dismissed";

/**
 * Merge Manager — every duplicate group the system currently identifies, where a
 * coach can dismiss a group or merge it, singly or in bulk.
 *
 * Nothing merges automatically. That decision is what makes the absence of an
 * unmerge tolerable: no record is destroyed that a coach did not look at first.
 */
export const DuplicatesSection = () => {
  const [candidates, setCandidates] = useState<DuplicateCandidate[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // "Every open group in the organization", held as a flag rather than as ids.
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [mergeAllJob, setMergeAllJob] = useState<MergeAllJob | null>(null);
  const acknowledgedJobIdRef = useRef<string | null>(readAcknowledgedJobId());
  const [isStartingMergeAll, setIsStartingMergeAll] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState<Tab>("open");

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [isBulkMerging, setIsBulkMerging] = useState(false);
  // Groups finished / total for the current bulk run. A select-all merge can be
  // hundreds of groups across several requests, so the dialog reports progress
  // instead of sitting on a spinner.
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });

  const [mergeTarget, setMergeTarget] = useState<DuplicateCandidate | null>(null);
  const [mergeModalProspects, setMergeModalProspects] = useState<ProspectResponse[]>([]);
  const [isLoadingMergeTarget, setIsLoadingMergeTarget] = useState(false);

  const { count, refresh: refreshCount } = useDuplicateCount();

  // Guards against a slower earlier page overwriting a newer one.
  const requestIdRef = useRef(0);

  const fetchPage = useCallback(async (pageNumber: number, append: boolean) => {
    const requestId = ++requestIdRef.current;
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    try {
      const response = await getDuplicateCandidates(pageNumber, PAGE_SIZE);
      if (requestId !== requestIdRef.current) return;

      setCandidates((current) =>
        append ? [...current, ...response.items] : response.items
      );
      setHasMore(response.hasMore);
      setTotalCount(response.totalCount);
      setPage(pageNumber);
      setError("");
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(getSafeErrorMessage(err, "Failed to load duplicates"));
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    void fetchPage(1, false);
  }, [fetchPage]);

  // A selection can only ever name groups that are still on screen.
  useEffect(() => {
    setSelected((current) => pruneSelection(current, candidates));
  }, [candidates]);

  // Whether the last observed state was a live run, so the list is reconciled
  // once on the transition rather than on every poll after it completes.
  const wasRunningRef = useRef(false);

  /**
   * A finished run the coach already dismissed stays dismissed. A live one always shows —
   * dismissing a summary is not a licence to hide a run that is still going.
   */
  const applyMergeAllJob = useCallback((job: MergeAllJob | null) => {
    const dismissed =
      job != null &&
      !isMergeAllRunning(job) &&
      job.jobId === acknowledgedJobIdRef.current;
    setMergeAllJob(dismissed ? null : job);
  }, []);

  const acknowledgeMergeAllJob = useCallback(() => {
    if (mergeAllJob) {
      acknowledgedJobIdRef.current = mergeAllJob.jobId;
      try {
        window.localStorage.setItem(ACKNOWLEDGED_JOB_KEY, mergeAllJob.jobId);
      } catch {
        // Worst case the summary reappears once; not worth surfacing.
      }
    }
    setMergeAllJob(null);
  }, [mergeAllJob]);

  // Read any run in flight on mount: a tab opened (or reopened) mid-run shows
  // the progress rather than nothing.
  useEffect(() => {
    let cancelled = false;
    const read = async () => {
      try {
        const job = await getMergeAllJob();
        if (cancelled) return;
        applyMergeAllJob(job);
        wasRunningRef.current = isMergeAllRunning(job);
      } catch {
        // A failed read is not worth a banner — the run is unaffected by whether
        // this tab can see it.
      }
    };
    void read();
    return () => {
      cancelled = true;
    };
  }, [applyMergeAllJob]);

  // Poll only while a run is open, and stop the moment it finishes.
  useEffect(() => {
    if (!isMergeAllRunning(mergeAllJob)) {
      // The run just ended: refetch, because it emptied the list underneath.
      if (wasRunningRef.current) {
        wasRunningRef.current = false;
        refreshCount();
        void fetchPage(1, false);
      }
      return;
    }

    wasRunningRef.current = true;
    const timer = setInterval(() => {
      void getMergeAllJob()
        .then(applyMergeAllJob)
        .catch(() => {
          // Skip this tick; the next one recovers.
        });
    }, MERGE_ALL_POLL_MS);
    return () => clearInterval(timer);
  }, [mergeAllJob, fetchPage, refreshCount, applyMergeAllJob]);

  const selectedCandidates = useMemo(
    () => candidates.filter((candidate) => selected.has(candidate.id)),
    [candidates, selected]
  );

  // "Everything" only once there are no unfetched pages left — with more to load,
  // a full-page selection is still a partial one and the checkbox must keep
  // offering to pull the rest in.
  /** Groups in the organization, which is what select-all covers. */
  const orgGroupCount = Math.max(totalCount, candidates.length, count);
  const selectionCount = selectAllMode ? orgGroupCount : selected.size;
  const mergeAllRunning = isMergeAllRunning(mergeAllJob);

  /**
   * Select every group in the organization.
   *
   * Deliberately a flag, not a set of ids. An organization can carry thousands
   * of groups; fetching and holding them to back a checkbox is work the client
   * should never do, and the merge itself goes to the server as a job.
   */
  const handleSelectAll = () => {
    if (selectAllMode) {
      setSelectAllMode(false);
      setSelected(new Set());
      return;
    }
    setSelectAllMode(true);
    setSelected(new Set());
  };

  /** Stop a run in flight. What it already merged stays merged. */
  const handleCancelMergeAll = async () => {
    try {
      const stopped = await cancelMergeAllDuplicates();
      setMergeAllJob(stopped);
      wasRunningRef.current = false;
      // Re-read: the groups it got through are gone from the server.
      refreshCount();
      void fetchPage(1, false);
    } catch (err) {
      setError(getSafeErrorMessage(err, "Could not stop the merge"));
    }
  };

  /** Hand the whole organization to the server; the run outlives this tab. */
  const handleMergeAll = async () => {
    setIsStartingMergeAll(true);
    try {
      const started = await startMergeAllDuplicates();
      wasRunningRef.current = true;
      setMergeAllJob(started);
      setSelectAllMode(false);
      setShowBulkConfirm(false);
    } catch (err) {
      setError(getSafeErrorMessage(err, "Could not start the merge"));
      setShowBulkConfirm(false);
    } finally {
      setIsStartingMergeAll(false);
    }
  };

  const setBusy = (candidateId: string, busy: boolean) =>
    setBusyIds((current) => {
      const next = new Set(current);
      if (busy) next.add(candidateId);
      else next.delete(candidateId);
      return next;
    });

  const removeCandidates = (candidateIds: string[]) => {
    const removed = new Set(candidateIds);
    setCandidates((current) => current.filter((candidate) => !removed.has(candidate.id)));
    setTotalCount((current) => Math.max(current - candidateIds.length, 0));
  };

  // ----- Dismiss -----

  const handleDismiss = async (candidate: DuplicateCandidate) => {
    setBusy(candidate.id, true);
    setRowErrors((current) => ({ ...current, [candidate.id]: "" }));
    try {
      await dismissDuplicateCandidate(candidate.id);
      removeCandidates([candidate.id]);
      refreshCount();
    } catch (err) {
      setRowErrors((current) => ({
        ...current,
        [candidate.id]: getSafeErrorMessage(err, "Failed to dismiss this group"),
      }));
    } finally {
      setBusy(candidate.id, false);
    }
  };

  // ----- Single merge (routes into the existing wizard) -----

  const handleMerge = async (candidate: DuplicateCandidate) => {
    setBusy(candidate.id, true);
    setIsLoadingMergeTarget(true);
    setRowErrors((current) => ({ ...current, [candidate.id]: "" }));
    try {
      // The wizard needs full prospect records to show the conflict step; the
      // group only carries the fields the list renders.
      const prospects = await Promise.all(
        candidate.members.map((member) => getProspect(member.prospectId))
      );
      setMergeModalProspects(prospects);
      setMergeTarget(candidate);
    } catch (err) {
      setRowErrors((current) => ({
        ...current,
        [candidate.id]: getSafeErrorMessage(err, "Failed to load these players"),
      }));
    } finally {
      setIsLoadingMergeTarget(false);
      setBusy(candidate.id, false);
    }
  };

  const closeMergeModal = () => {
    setMergeTarget(null);
    setMergeModalProspects([]);
  };

  // The wizard reports which records survived; the Merge Manager only needs to know the
  // group is resolved. Refetch rather than just dropping the row: a merge soft-deletes
  // prospects that may also be members of other loaded groups, and those rows are now
  // stale. (Dismissal deletes nothing, so it does not need this.)
  const handleMergeSuccess = () => {
    if (mergeTarget) {
      removeCandidates([mergeTarget.id]);
    }
    closeMergeModal();
    refreshCount();
    void fetchPage(1, false);
  };

  // ----- Bulk merge -----

  const handleBulkMerge = async () => {
    const groups = selectedCandidates;
    setIsBulkMerging(true);
    setBulkProgress({ done: 0, total: groups.length });

    // One run id for the whole run, including every batch: the server keys
    // idempotency on run + candidate, so a retried batch replays rather than
    // double-merging.
    const runId = crypto.randomUUID();
    const succeeded: string[] = [];
    const failures: { candidateId: string; errorMessage?: string | null }[] = [];
    let transportError = "";

    for (const batch of chunk(groups, BULK_MERGE_BATCH_SIZE)) {
      try {
        const response = await bulkMergeProspects({
          runId,
          groups: buildBulkMergeGroups(batch),
        });

        for (const result of response.results) {
          if (result.success) succeeded.push(result.candidateId);
          else failures.push(result);
        }
        setBulkProgress((current) => ({ ...current, done: current.done + batch.length }));
      } catch (err) {
        // Stop at the first failed request — the batches already committed stand.
        transportError = getSafeErrorMessage(err, "Bulk merge failed");
        break;
      }
    }

    removeCandidates(succeeded);
    // Failures stay on screen with the reason on their own row — a partial run
    // must be visible per group, not collapsed into one banner.
    setRowErrors((current) => {
      const next = { ...current };
      for (const failure of failures) {
        next[failure.candidateId] =
          failure.errorMessage ?? "This group could not be merged.";
      }
      return next;
    });

    setShowBulkConfirm(false);
    setIsBulkMerging(false);
    refreshCount();

    if (transportError) {
      setError(
        succeeded.length > 0
          ? `Merged ${succeeded.length} of ${groups.length} groups, then the request failed. Try the rest again.`
          : transportError
      );
    }

    // Merged-away prospects can be members of groups this run didn't touch; refetch so
    // those stale rows go rather than failing on a later merge attempt. Row errors are
    // keyed by candidate id and survive the refetch.
    if (succeeded.length > 0) {
      void fetchPage(1, false);
    }
  };

  useTour("duplicates", { ready: !isLoading, hasData: candidates.length > 0 });

  // ----- Render -----

  return (
    <ErrorBoundary>
      <section className="duplicates">
        <header className="duplicates__header">
          <div {...tourAnchor("duplicates.title")}>
            <h1 className="duplicates__title">Merge Manager</h1>
            <p className="duplicates__subtitle">
              Players that look like the same person. Nothing merges on its own.
            </p>
          </div>
        </header>

        <div className="duplicates__tabs" role="tablist" {...tourAnchor("duplicates.tabs")}>
          <button
            role="tab"
            aria-selected={tab === "open"}
            className={`duplicates__tab${tab === "open" ? " duplicates__tab--active" : ""}`}
            onClick={() => setTab("open")}
          >
            Open{count > 0 ? ` (${count})` : ""}
          </button>
          <button
            role="tab"
            aria-selected={tab === "dismissed"}
            className={`duplicates__tab${tab === "dismissed" ? " duplicates__tab--active" : ""}`}
            onClick={() => setTab("dismissed")}
          >
            Dismissed
          </button>
        </div>

        {tab === "dismissed" ? (
          <DismissedDuplicatesPanel
            onRestored={() => {
              refreshCount();
              void fetchPage(1, false);
            }}
          />
        ) : (
          <>
            {error && (
              <p className="duplicates__error" role="alert">
                {error}
              </p>
            )}

            {/* A server-side run, live or just finished. Above the list because
                while it runs the list underneath is being emptied. */}
            {mergeAllJob && (
              <div className="duplicates__job" role="status" data-testid="merge-all-banner">
                {mergeAllRunning ? (
                  <>
                    <span>
                      {mergeAllJob.stalled
                        ? `Paused at ${mergeAllJob.processedGroups.toLocaleString()} of ${mergeAllJob.totalGroups.toLocaleString()}. It will pick itself back up in a minute, or stop it and start again.`
                        : `Merging ${mergeAllJob.processedGroups.toLocaleString()} of ${mergeAllJob.totalGroups.toLocaleString()}… you can leave this page.`}
                    </span>
                    {/* Always available while a run is open. A run interrupted by
                        a deploy used to leave a progress bar with no way out. */}
                    <Button
                      variant="secondary"
                      onClick={() => void handleCancelMergeAll()}
                    >
                      Stop
                    </Button>
                  </>
                ) : (
                  <>
                    <span>
                      {mergeAllJob.status === "cancelled"
                        ? `Stopped after merging ${mergeAllJob.succeededCount.toLocaleString()} ${
                            mergeAllJob.succeededCount === 1 ? "group" : "groups"
                          }. The rest are still listed.`
                        : mergeAllJob.status === "failed"
                        ? mergeAllJob.errorMessage ??
                          "The run stopped before finishing. Start it again for the rest."
                        : `Merged ${mergeAllJob.succeededCount.toLocaleString()} ${
                            mergeAllJob.succeededCount === 1 ? "group" : "groups"
                          }${
                            mergeAllJob.failedCount > 0
                              ? `. ${mergeAllJob.failedCount.toLocaleString()} could not be merged and are still listed.`
                              : "."
                          }`}
                    </span>
                    <button
                      type="button"
                      className="duplicates__job-dismiss"
                      onClick={acknowledgeMergeAllJob}
                      aria-label="Dismiss"
                    >
                      ×
                    </button>
                  </>
                )}
              </div>
            )}

            {candidates.length > 0 && !mergeAllRunning && (
              <div className="duplicates__bulk-bar" {...tourAnchor("duplicates.bulk")}>
                <label className="duplicates__select-all">
                  <input
                    type="checkbox"
                    checked={selectAllMode}
                    onChange={handleSelectAll}
                  />
                  {/* Every group in the organization. Selecting them costs nothing
                      — no ids are held and no pages are fetched; the merge itself
                      runs on the server. */}
                  {`Select all ${orgGroupCount.toLocaleString()}`}
                </label>
                <Button
                  disabled={selectionCount === 0}
                  onClick={() => setShowBulkConfirm(true)}
                >
                  {selectAllMode
                    ? `Merge all ${selectionCount.toLocaleString()}`
                    : `Merge ${selectionCount} selected`}
                </Button>
              </div>
            )}

            {isLoading ? (
              <p className="duplicates__empty">Loading duplicates…</p>
            ) : candidates.length === 0 ? (
              <p className="duplicates__empty" {...tourAnchor("duplicates.list")}>
                No duplicates found. New ones appear here as players are added or edited.
              </p>
            ) : (
              <ul className="duplicates__list" {...tourAnchor("duplicates.list")}>
                {candidates.map((candidate, index) => (
                  <DuplicateGroupCard
                    key={candidate.id}
                    isFirst={index === 0}
                    candidate={candidate}
                    selected={selected.has(candidate.id)}
                    busy={busyIds.has(candidate.id) || isLoadingMergeTarget}
                    error={rowErrors[candidate.id] || undefined}
                    onToggleSelect={(id) => {
                      // Picking one group is a different intent from "everything".
                      setSelectAllMode(false);
                      setSelected((current) => toggleSelection(current, id));
                    }}
                    onMerge={handleMerge}
                    onDismiss={handleDismiss}
                  />
                ))}
              </ul>
            )}

            {hasMore && (
              <div className="duplicates__load-more">
                <Button
                  variant="secondary"
                  loading={isLoadingMore}
                  onClick={() => void fetchPage(page + 1, true)}
                >
                  Load more
                </Button>
              </div>
            )}
          </>
        )}

        {showBulkConfirm && (
          <BulkMergeConfirmDialog
            candidates={selectedCandidates}
            // Select-all names a scale rather than a reviewable list: there is no
            // honest way to itemize thousands of groups.
            allGroupCount={selectAllMode ? orgGroupCount : undefined}
            isSubmitting={isBulkMerging || isStartingMergeAll}
            progress={isBulkMerging ? bulkProgress : undefined}
            onCancel={() => setShowBulkConfirm(false)}
            onConfirm={() =>
              selectAllMode ? void handleMergeAll() : void handleBulkMerge()
            }
          />
        )}

        {mergeTarget && mergeModalProspects.length >= 2 && (
          <MergeProspectsModal
            isOpen
            prospects={mergeModalProspects}
            // The oldest record, so the wizard opens on the one this group's card
            // marked "Survives" instead of the wizard's most-complete default.
            preferredSurvivorId={pickBulkSurvivor(mergeTarget)?.prospectId}
            onClose={closeMergeModal}
            onSuccess={handleMergeSuccess}
            onStaleData={() => void fetchPage(1, false)}
          />
        )}
      </section>
    </ErrorBoundary>
  );
};
